/**
 * Brazil pilot partner desk — delivery, food and the four-eyes publication (W2-DESK-ORDERS, D366; ADR-0047
 * amendment) — country-pack data.
 *
 * Owner decision D363 #4 opened delivery and food to the desk in the first cycle. This module is the Brazil
 * realization of the neutral, governed policies that decide them per pilot market, and every row here is published
 * under FOUR EYES by the seed (`@vello/pack-seed` `seedFourEyesPolicies`: a maker submits, a different approver
 * attests, bound to the version's content hash — the pattern the pilot tariffs use). The partner-desk readers resolve
 * ONLY attested versions, so these rows are the governing ones:
 *
 *  - `payment.partnerBookingMethods` **version 2** (`[cash, partner_account]`, unchanged set). D365 published version
 *    1 through the ordinary pack publisher, without four eyes (the D365 review's R3). Version 1 stays immutable and
 *    stays in history; it simply governs nothing once the reader is four-eyes. Nothing is mutated.
 *  - `partner.deskOrderVerticals` v1 — the desk may order `delivery` and `food` in both pilot markets.
 *  - `delivery.sendTerms` — the pilot Send card (BRL centavos), prohibited items, no recipient pre-consent (the guest
 *    is at the desk), PIN proof. ILLUSTRATIVE pilot values, flat per city: the `@vello/delivery-pricing` kernel prices
 *    distance in whole minor units per METER (at least R$ 10,00/km), too coarse for a small-city card, so the pilot
 *    card carries no distance component — a per-km granularity is a kernel follow-up, recorded in the D366 handoff
 *    (D856 added the governed `send.rateCard` for that). v2 (D854, the description keywords) offered a moto and a
 *    car; **v3 (owner 2026-11-01, D889; D890) offers the moto only** — today's Send is a moto collecting one
 *    small item, car / utility / truck delivery is a future multi-stop B2B program and a van is passenger only — so a
 *    car Send can no longer be quoted (`VEHICLE_CLASS_UNAVAILABLE`). The pack keeps v2 beside v3 (v2 until v3's
 *    instant), so a re-run of the seed on a base that holds v2 adds v3 and nothing else.
 *  - `food.feePolicy` v1 — the pilot food fee policy (a flat delivery fee and a small-order surcharge, no service fee,
 *    tax 0 bps — ILLUSTRATIVE; the real tax treatment is a country-pack decision the owner has not taken).
 *
 * It also carries the pilot's test data for the desk: the hotel's door in each pilot market, and a small, TAGGED,
 * illustrative catalog for the pilot test store (published through the catalog's own version publisher by the seed).
 *
 * This module imports nothing from `./brazil.js` (which imports it), so the market ids are spelled here as data.
 */
import { money } from '@vello/core';
import { partnerBookingPaymentMethodsSetting, marketId } from '@vello/config';
import { deliverySendTermsSetting, deskOrderVerticalsSetting, foodFeePolicySetting } from '@vello/business';
const BRL = (centavos) => money(centavos, 'BRL');
/** The pilot markets whose partner desks order delivery and food (D353 #4, D363 #4). */
export const BRAZIL_PARTNER_DESK_ORDER_MARKETS = [marketId('br:paracatu'), marketId('br:patos-de-minas')];
/** The instant the D366 pilot rows take effect (the decision date, UTC). */
export const BRAZIL_PARTNER_DESK_ORDERS_EFFECTIVE_FROM = '2026-09-17T00:00:00Z';
/** The maker and the approver the seed records on every four-eyes row (distinct identities — ADR-0042). */
export const BRAZIL_FOUR_EYES_SEED_MAKER = 'seed:policy';
export const BRAZIL_FOUR_EYES_SEED_APPROVER = 'seed:governance';
/** When `delivery.sendTerms` v3 (moto only, owner D889) takes effect: the decision date, UTC. */
export const BRAZIL_SEND_TERMS_MOTO_ONLY_EFFECTIVE_FROM = '2026-11-01T00:00:00Z';
/** The version of the pilot markets' `delivery.sendTerms` the current card is (D890: v3, moto only). */
export const BRAZIL_SEND_TERMS_VERSION = 3;
/**
 * The pilot Send card - v3 (owner 2026-11-01, D889: "Deixa só moto. Nem carro, é só moto."): flat per city, BRL centavos, and the MOTO is the only vehicle
 * it offers (`vehicleSurcharges: { motorbike }`). Illustrative.
 */
export function brazilPilotSendTerms() {
    const v2 = brazilPilotSendTermsV2();
    return { ...v2, rateCard: { ...v2.rateCard, vehicleSurcharges: { motorbike: BRL(0n) } } };
}
/** The pilot Send card v2 (D854): the v3 card plus a car at R$ 5,00 more. Immutable history: it governs only until v3's instant. */
export function brazilPilotSendTermsV2() {
    return {
        rateCard: {
            currency: 'BRL',
            base: BRL(700n),
            perMeter: BRL(0n),
            sizeBands: [
                { band: 'small', maxVolumeMm3: 30_000_000, maxWeightGrams: 5_000 },
                { band: 'medium', maxVolumeMm3: 80_000_000, maxWeightGrams: 15_000 },
                { band: 'large', maxVolumeMm3: 200_000_000, maxWeightGrams: 30_000 },
            ],
            sizeSurcharges: { small: BRL(0n), medium: BRL(300n), large: BRL(800n), oversized: BRL(1500n) },
            vehicleSurcharges: { motorbike: BRL(0n), car: BRL(500n) },
            serviceLevelSurcharges: { standard: BRL(0n), express: BRL(400n) },
            waitPerSecond: BRL(0n),
            freeWaitSeconds: 600,
            returnLegSurcharge: BRL(500n),
            quoteTtlSeconds: 600,
            coverage: { version: 'br-pilot-send-coverage-v1', maxCoveredValue: BRL(20000n), feeBasisPoints: 0 },
        },
        prohibitedCategories: ['weapons', 'hazardous_materials', 'illegal_substances', 'live_animals'],
        // D854 L2-011: the rider app declares no category, so a Send's description is screened for these words (whole words,
        // accent- and case-insensitive). Portuguese terms of the Brazil pack; another country publishes its own list.
        prohibitedKeywords: [
            'arma de fogo', 'armas de fogo', 'arma branca', 'armas brancas', 'revolver', 'pistola de fogo', 'espingarda', 'municao', 'municoes',
            'explosivo', 'explosivos', 'dinamite', 'polvora',
            'droga', 'drogas', 'maconha', 'cocaina', 'crack', 'heroina', 'lsd', 'entorpecente', 'entorpecentes',
            'animal vivo', 'animais vivos',
        ],
        // The guest is at the desk when the Send is ordered; the desk gives them the handover PIN.
        requireRecipientConsent: false,
        proofMethod: 'pin',
    };
}
/** The pilot food fee policy, BRL centavos. Illustrative (tax 0 bps until the owner decides the tax treatment). */
export function brazilPilotFoodFeePolicy() {
    return { currency: 'BRL', serviceFeeBps: 0, deliveryFee: BRL(600n), smallOrderThreshold: BRL(2500n), smallOrderFee: BRL(300n), taxBps: 0 };
}
/** Every row the Brazil pack publishes under four eyes (D366). */
export function brazilFourEyesPolicySettings() {
    const common = { scope: 'market', effectiveFrom: BRAZIL_PARTNER_DESK_ORDERS_EFFECTIVE_FROM };
    return BRAZIL_PARTNER_DESK_ORDER_MARKETS.flatMap((market) => {
        const scopeRef = market;
        return [
            // A NEW version: v1 (D365, ordinary publisher) is immutable and stays in history; v2 is the four-eyes one.
            partnerBookingPaymentMethodsSetting({ ...common, scopeRef, methods: ['cash', 'partner_account'], version: 2 }),
            deskOrderVerticalsSetting({ ...common, scopeRef, verticals: ['delivery', 'food'], version: 1 }),
            // v2 (D854): v1 is immutable history; v2 adds the description keywords.
            deliverySendTermsSetting({ ...common, scopeRef, terms: brazilPilotSendTermsV2(), version: 2 }),
            // v3 (owner 2026-11-01, D889): the moto only - a car Send is no longer offered. From the decision date; v2 stays as history.
            deliverySendTermsSetting({ ...common, scopeRef, terms: brazilPilotSendTerms(), version: BRAZIL_SEND_TERMS_VERSION, effectiveFrom: BRAZIL_SEND_TERMS_MOTO_ONLY_EFFECTIVE_FROM }),
            foodFeePolicySetting({ ...common, scopeRef, policy: brazilPilotFoodFeePolicy(), version: 1 }),
        ];
    });
}
/** The pilot hotel's door in each pilot market (test data; the addresses are illustrative). */
export function brazilPilotPartnerPickupPoints() {
    return [
        { marketId: 'br:paracatu', latitude: -17.2252, longitude: -46.8711, address: 'Recepção — Pousada Teste Piloto (Paracatu)' },
        { marketId: 'br:patos-de-minas', latitude: -18.5789, longitude: -46.5181, address: 'Recepção — Pousada Teste Piloto (Patos de Minas)' },
    ];
}
/** A small, illustrative catalog for the pilot test store (BRL centavos). Tagged test data, never a real menu. */
export function brazilPilotFoodCatalog() {
    return {
        menuId: 'pilot-menu-paracatu-centro',
        name: 'Lanchonete Teste Piloto',
        effectiveFrom: BRAZIL_PARTNER_DESK_ORDERS_EFFECTIVE_FROM,
        categories: [
            {
                categoryId: 'pilot-cat-lanches',
                name: 'Lanches',
                items: [
                    { itemId: 'pilot-item-pao-de-queijo', name: 'Pão de queijo (6 un.)', priceMinor: 1200n },
                    { itemId: 'pilot-item-sanduiche-natural', name: 'Sanduíche natural', priceMinor: 1800n },
                    { itemId: 'pilot-item-suco-laranja', name: 'Suco de laranja 500 ml', priceMinor: 900n },
                ],
            },
            {
                categoryId: 'pilot-cat-refeicoes',
                name: 'Refeições',
                items: [{ itemId: 'pilot-item-prato-feito', name: 'Prato feito', priceMinor: 2800n }],
            },
        ],
    };
}
//# sourceMappingURL=partner-desk-orders.js.map